import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  CircleDollarSign,
  Clock,
  Inbox,
  Link2,
  Mail,
  Package,
  Save,
  Send,
  Truck,
  Undo2,
  X,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { generateId, useApp, type Product } from "@/context/AppContext";
import { imageUrl } from "@/features/imageUpload";
import {
  cancelReservationOffer,
  fetchSellerReservations,
  patchReservation,
  patchReservationItem,
  respondToBuyerCounter,
  sendReservationOffer,
  type ReservationAction,
  type ReservationItemAction,
  type SellerReservation,
} from "@/features/reservations";
import { asRarity, rarityConfig } from "@/components/shop/cardVisuals";
import { isReservationItemUnavailable } from "@/features/storefront";

type ItemView = SellerReservation["cards"][number];

function itemPrice(c: ItemView): number {
  return c.requestedPrice ?? c.currentMarketPrice ?? 0;
}

// Magic color → hex for the thumb border gradient. Copies the palette
// the import wide-row uses (apps/web/src/routes/tools.imports.$id.review.tsx)
// so MTG cards get the same visual cue across surfaces.
const MTG_COLORS: Record<string, string> = {
  W: "#f6efd2",
  U: "#9fd0f0",
  B: "#3f3a3a",
  R: "#f0998d",
  G: "#a3cba0",
};

function mtgBorderGradient(colors: string[] | undefined | null): string | null {
  if (!colors || colors.length === 0) return null;
  if (colors.length === 1) return MTG_COLORS[colors[0]] ?? "#5a5a5a";
  const stops = colors.map(
    (c, i) =>
      `${MTG_COLORS[c] ?? "#5a5a5a"} ${(i / colors.length) * 100}% ${((i + 1) / colors.length) * 100}%`,
  );
  return `linear-gradient(135deg, ${stops.join(", ")})`;
}

// Format a Date for `<input type="datetime-local">` (local time, no
// timezone). The browser parses this back as local time when the user
// changes it; `new Date(localValue).toISOString()` then yields the
// correct UTC ISO for the server.
function formatLocalDateTime(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const Route = createFileRoute("/reservations")({
  component: ReservationsPage,
});

// Status styles cover both the legacy lifecycle (Pending/Accepted/etc.)
// and the TNK-127 phase 2 offer lifecycle (UnderReview/OfferSent/
// AwaitingPayment/Paid). Cancelled re-uses the Declined treatment.
const STATUS_STYLE: Record<string, string> = {
  Pending: "bg-warning text-warning-foreground",
  Accepted: "bg-success text-success-foreground",
  Declined: "bg-destructive/15 text-destructive",
  Expired: "bg-muted text-muted-foreground",
  Completed: "bg-success text-success-foreground",
  UnderReview: "bg-secondary text-secondary-foreground",
  OfferSent: "bg-warning text-warning-foreground",
  AwaitingPayment: "bg-primary text-primary-foreground",
  Paid: "bg-success text-success-foreground",
  Cancelled: "bg-destructive/15 text-destructive",
};

// Reservation is closed (no further action possible) in these states.
const TERMINAL_RESERVATION_STATUSES = new Set([
  "Declined",
  "Expired",
  "Completed",
  "Cancelled",
  "Paid",
]);

function ReservationsPage() {
  const [reservations, setReservations] = useState<SellerReservation[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // TNK-127 phase 3 — Mark Paid turns the held cards into a bundle product
  // (existing optimistic path) then hands off to the sale-record flow.
  const { addProductWithPackaging } = useApp();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setReservations(await fetchSellerReservations());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t load reservations.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (
    id: string,
    action: ReservationAction,
    payload?: {
      sellerNote?: string;
      adminNotes?: string;
      paymentLinkUrl?: string;
    },
  ) => {
    setBusyId(id);
    try {
      await patchReservation(id, action, payload);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  // TNK-127 phase 3 — convert an AwaitingPayment reservation: build a bundle
  // product from the held cards (flips them Reserved → In Package via the
  // existing path), close the reservation to Paid, then open the sale-record
  // form pre-filled. The operator finishes recording the sale there.
  const markPaidAndBundle = async (r: SellerReservation) => {
    const heldItems = r.cards.filter(
      (c) => c.itemStatus === "held" && c.stillAvailable,
    );
    // TNK-212 phase 2b — only LOOSE cards get packaged into a new product;
    // a bundle item is already its own product (its `id` is the productId, not
    // a cardId — don't bundle it as one). The bundle's product is flipped Sold
    // server-side by the mark-paid PATCH.
    const cardIds = heldItems
      .filter((c) => c.kind !== "bundle")
      .map((c) => c.id);
    const heldBundle = heldItems.find(
      (c) => c.kind === "bundle" && c.productId,
    );
    if (cardIds.length === 0 && !heldBundle) {
      setError("No held items to complete on this reservation.");
      return;
    }
    const total =
      r.currentOffer?.total ?? heldItems.reduce((s, c) => s + itemPrice(c), 0);
    setBusyId(r.id);
    try {
      // Loose cards → a new bundle product to record the sale against. With
      // no loose cards (bundle-only), record against the existing bundle.
      const productId =
        cardIds.length > 0 ? generateId("PROD") : heldBundle!.productId!;
      if (cardIds.length > 0) {
        const product: Product = {
          id: productId,
          productType: "Custom",
          name: `Reservation — ${r.buyerName || r.buyerEmail}`,
          quantity: 1,
          cardCount: cardIds.length,
          cardIds,
          sourceLotId: "",
          costBasis: 0,
          packagingCost: 0,
          targetSalePrice: total,
          listingTitle: "",
          description: `From storefront reservation ${r.id}`,
          storageLocation: "",
          status: "Draft",
          createdAt: new Date().toISOString(),
        };
        addProductWithPackaging(product);
      }
      await patchReservation(r.id, "mark-paid");
      await navigate({
        to: "/sales/create",
        search: { productId, salePrice: total.toFixed(2) },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Mark paid failed.");
      setBusyId(null);
    }
  };

  const itemAct = async (
    reservationId: string,
    itemId: string,
    action: ReservationItemAction,
  ) => {
    setBusyId(reservationId);
    try {
      await patchReservationItem(reservationId, itemId, action);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  // TNK-127 phase 2 — seller sends or cancels an offer. Same shape as
  // the existing `act` / `itemAct` (busy state + load on success).
  const sendOffer = async (
    id: string,
    body: Parameters<typeof sendReservationOffer>[1],
  ) => {
    setBusyId(id);
    try {
      await sendReservationOffer(id, body);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Send offer failed.");
    } finally {
      setBusyId(null);
    }
  };

  const cancelOffer = async (id: string, offerId: string) => {
    setBusyId(id);
    try {
      await cancelReservationOffer(id, offerId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Cancel offer failed.");
    } finally {
      setBusyId(null);
    }
  };

  // TNK-127 phase 4 — seller accepts/declines a buyer's counter-offer.
  const respondCounter = async (
    id: string,
    offerId: string,
    action: "accept" | "decline",
  ) => {
    setBusyId(id);
    try {
      await respondToBuyerCounter(id, offerId, action);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <BackButton fallback={{ to: "/dashboard" }} />
      <h1 className="text-lg font-semibold text-foreground">Reservations</h1>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {reservations === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : reservations.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No reservations yet. When a customer reserves cards from your
            storefront, they’ll show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reservations.map((r) => (
            <ReservationCard
              key={r.id}
              reservation={r}
              busy={busyId === r.id}
              onAct={(action, payload) => void act(r.id, action, payload)}
              onItemAct={(itemId, action) => void itemAct(r.id, itemId, action)}
              onSendOffer={(body) => void sendOffer(r.id, body)}
              onCancelOffer={(offerId) => void cancelOffer(r.id, offerId)}
              onRespondCounter={(offerId, action) =>
                void respondCounter(r.id, offerId, action)
              }
              onMarkPaid={() => void markPaidAndBundle(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReservationCard({
  reservation: r,
  busy,
  onAct,
  onItemAct,
  onSendOffer,
  onCancelOffer,
  onRespondCounter,
  onMarkPaid,
}: {
  reservation: SellerReservation;
  busy: boolean;
  onAct: (
    action: ReservationAction,
    payload?: {
      sellerNote?: string;
      adminNotes?: string;
      paymentLinkUrl?: string;
    },
  ) => void;
  onItemAct: (itemId: string, action: ReservationItemAction) => void;
  onSendOffer: (body: {
    subtotal: number;
    discount?: number;
    shipping?: number;
    total: number;
    message?: string;
    expiresAt: string;
  }) => void;
  onCancelOffer: (offerId: string) => void;
  onRespondCounter: (offerId: string, action: "accept" | "decline") => void;
  onMarkPaid: () => void;
}) {
  // TNK-163 — total excludes items the seller has flagged as removed
  // so the "$N" line matches what the buyer would actually pay.
  const activeItems = r.cards.filter(
    (c) =>
      c.itemStatus !== "removed_unavailable" && c.itemStatus !== "released",
  );
  const removedItems = r.cards.filter(
    (c) =>
      c.itemStatus === "removed_unavailable" || c.itemStatus === "released",
  );
  const total = activeItems.reduce((s, c) => s + itemPrice(c), 0);
  // TNK-127 — "no longer available" counts items whose card was sold, bundled,
  // hard-held by another reservation, or deleted (read-side; selling never
  // mutates other reservations). Distinct from the seller's own per-item decline.
  const driftCount = r.cards.filter(
    (c) =>
      isReservationItemUnavailable(c) &&
      c.itemStatus !== "removed_unavailable" &&
      c.itemStatus !== "released",
  ).length;
  const [adminNotesDraft, setAdminNotesDraft] = useState(r.adminNotes ?? "");
  const notesDirty = adminNotesDraft !== (r.adminNotes ?? "");
  // TNK-127 phase 3 — payment link draft + count of cards that would bundle.
  const [payLinkDraft, setPayLinkDraft] = useState(r.paymentLinkUrl ?? "");
  const payLinkDirty = payLinkDraft.trim() !== (r.paymentLinkUrl ?? "");
  const heldCount = r.cards.filter(
    (c) => c.itemStatus === "held" && c.stillAvailable,
  ).length;
  const isOpen = !TERMINAL_RESERVATION_STATUSES.has(r.status);
  // TNK-127 phase 2 — inline send-offer form. Defaults: subtotal pre-
  // filled from active items' requestedPrice sum, expires 48h out.
  const defaultSubtotal = activeItems
    .reduce((s, c) => s + itemPrice(c), 0)
    .toFixed(2);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerSubtotal, setOfferSubtotal] = useState(defaultSubtotal);
  const [offerDiscount, setOfferDiscount] = useState("0");
  const [offerShipping, setOfferShipping] = useState("0");
  const [offerMessage, setOfferMessage] = useState("");
  const [offerExpiresAt, setOfferExpiresAt] = useState(() =>
    formatLocalDateTime(new Date(Date.now() + 48 * 60 * 60 * 1000)),
  );
  const offerTotal =
    (Number(offerSubtotal) || 0) -
    (Number(offerDiscount) || 0) +
    (Number(offerShipping) || 0);

  const currentOffer = r.currentOffer;
  const hasActiveOffer =
    currentOffer?.status === "sent" || currentOffer?.status === "accepted";
  const canSendOffer = isOpen && !hasActiveOffer && activeItems.length > 0;
  // TNK-127 phase 4 — a `sent` buyer counter is awaiting the seller's response
  // (accept / decline / counter-back).
  const isBuyerCounter =
    currentOffer?.status === "sent" && currentOffer?.createdByType === "buyer";

  return (
    <article className="space-y-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                STATUS_STYLE[r.status] ?? "bg-muted text-muted-foreground"
              }`}
            >
              {r.status}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {new Date(r.requestedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="mt-1 text-sm font-semibold text-foreground">
            {r.buyerName || "Customer"}
          </div>
          <a
            href={`mailto:${r.buyerEmail}`}
            className="text-[12px] text-primary hover:underline"
          >
            {r.buyerEmail}
          </a>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-sm font-semibold text-foreground">
            ${total.toFixed(2)}
          </div>
          <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            {r.pickupPreference === "pickup" ? (
              <Package className="h-3 w-3" />
            ) : r.pickupPreference === "ship" ? (
              <Truck className="h-3 w-3" />
            ) : null}
            {r.pickupPreference}
          </div>
        </div>
      </div>

      {driftCount > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-[11px] font-semibold text-warning">
          <AlertTriangle className="h-3 w-3" />
          {driftCount} card{driftCount === 1 ? "" : "s"} in this reservation{" "}
          {driftCount === 1 ? "is" : "are"} no longer available (sold, bundled,
          or held on another reservation) — review before responding.
        </div>
      )}

      {activeItems.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-3 text-center text-[12px] text-muted-foreground">
          Every item has been removed. Decline this reservation to close it.
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {r.cards.map((c) => (
          <CardTile
            key={c.itemId ?? c.id}
            card={c}
            busy={busy}
            canRemove={isOpen}
            onItemAct={onItemAct}
          />
        ))}
      </div>

      {removedItems.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          {removedItems.length} item
          {removedItems.length === 1 ? "" : "s"} removed from this reservation.
        </p>
      )}

      {r.buyerNote && (
        <p className="rounded-md bg-muted px-3 py-2 text-[12px] text-foreground">
          “{r.buyerNote}”
        </p>
      )}
      {r.shippingAddress?.address && (
        <p className="text-[12px] text-muted-foreground">
          Ship to: {r.shippingAddress.address}
        </p>
      )}

      {/* TNK-127 phase 1 — admin notes: never shown to the buyer.
          Save sends `action: "notes-only"` so saving notes during review
          doesn't change the reservation's lifecycle status. */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Admin notes
          <span className="ml-1 normal-case text-muted-foreground/70">
            (private — not visible to the buyer)
          </span>
        </label>
        <textarea
          value={adminNotesDraft}
          onChange={(e) => setAdminNotesDraft(e.target.value)}
          rows={2}
          placeholder="Internal review notes — verify condition, prior buyer, etc."
          className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
        />
        {notesDirty && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onAct("notes-only", { adminNotes: adminNotesDraft })}
            className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2.5 py-1 text-[11px] font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-3 w-3" />
            Save notes
          </button>
        )}
      </div>

      {/* TNK-127 phase 2 — current offer panel. Renders when an offer
          has been sent (or accepted), so the seller can see what's
          outstanding + cancel before the buyer acts. */}
      {currentOffer && (
        <div
          className={`rounded-md border px-3 py-2.5 text-[12px] ${
            currentOffer.status === "sent"
              ? "border-warning/40 bg-warning/10"
              : currentOffer.status === "accepted"
                ? "border-success/40 bg-success/10"
                : "border-border bg-muted/40"
          }`}
        >
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 font-semibold text-foreground">
              <Mail className="h-3 w-3" />
              {isBuyerCounter
                ? "Buyer countered — your move"
                : currentOffer.status === "sent"
                  ? "Offer sent — awaiting buyer"
                  : currentOffer.status === "accepted"
                    ? "Buyer accepted — awaiting payment"
                    : currentOffer.status === "declined"
                      ? "Buyer declined this offer"
                      : currentOffer.status === "cancelled"
                        ? "You cancelled this offer"
                        : currentOffer.status === "expired"
                          ? "This offer expired"
                          : `Offer ${currentOffer.status}`}
            </div>
            {currentOffer.status === "sent" &&
              currentOffer.createdByType === "admin" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onCancelOffer(currentOffer.id)}
                  className="inline-flex items-center gap-1 rounded border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground hover:bg-muted disabled:opacity-50"
                >
                  <Undo2 className="h-2.5 w-2.5" />
                  Cancel offer
                </button>
              )}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-4">
            <div>
              <span className="text-muted-foreground/80">Subtotal</span>{" "}
              <span className="font-medium text-foreground">
                ${currentOffer.subtotal.toFixed(2)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground/80">Discount</span>{" "}
              <span className="font-medium text-foreground">
                ${currentOffer.discount.toFixed(2)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground/80">Shipping</span>{" "}
              <span className="font-medium text-foreground">
                ${currentOffer.shipping.toFixed(2)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground/80">Total</span>{" "}
              <span className="font-semibold text-foreground">
                ${currentOffer.total.toFixed(2)}
              </span>
            </div>
          </div>
          {currentOffer.status === "sent" && (
            <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock className="h-2.5 w-2.5" />
              Expires {new Date(currentOffer.expiresAt).toLocaleString()}
            </div>
          )}
          {currentOffer.message && (
            <p className="mt-1.5 border-t border-border/60 pt-1.5 text-[11px] text-foreground/80">
              “{currentOffer.message}”
            </p>
          )}
          {/* TNK-127 phase 4 — respond to the buyer's counter. Counter opens the
              send-offer form, which now supersedes the buyer's counter. */}
          {isBuyerCounter && (
            <div className="mt-2 flex flex-wrap gap-2 border-t border-border/60 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onRespondCounter(currentOffer.id, "accept")}
                className="inline-flex items-center gap-1.5 rounded-md bg-success px-3 py-1.5 text-xs font-semibold text-success-foreground hover:opacity-90 disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" />
                Accept ${currentOffer.total.toFixed(2)}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setShowOfferForm(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                Counter
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onRespondCounter(currentOffer.id, "decline")}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
                Decline
              </button>
            </div>
          )}
        </div>
      )}

      {/* TNK-127 phase 3 — payment hand-off. Shown once the buyer has
          accepted an offer (AwaitingPayment). Paste an optional manual
          payment link (no platform integration), then Mark Paid → which
          turns the held cards into a bundle product and opens the sale
          form to record it. */}
      {r.status === "AwaitingPayment" && (
        <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
            Send payment method
          </div>
          <p className="text-[10px] text-muted-foreground">
            The buyer accepted — send them a way to pay, then mark it paid.
          </p>
          <label className="block space-y-1 text-[10px] text-muted-foreground">
            Payment link (optional) — PayPal.me, Stripe link, etc. The buyer
            sees a “Pay now” button.
            <div className="flex gap-2">
              <input
                type="url"
                value={payLinkDraft}
                onChange={(e) => setPayLinkDraft(e.target.value)}
                placeholder="https://…"
                className="flex-1 rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
              {payLinkDirty && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    onAct("notes-only", {
                      paymentLinkUrl: payLinkDraft.trim(),
                    })
                  }
                  className="inline-flex items-center gap-1 rounded-md bg-secondary px-2.5 py-1 text-[11px] font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  <Link2 className="h-3 w-3" />
                  Save
                </button>
              )}
            </div>
          </label>
          <button
            type="button"
            disabled={busy || heldCount === 0}
            onClick={onMarkPaid}
            className="inline-flex items-center gap-1.5 rounded-md bg-success px-3 py-1.5 text-xs font-semibold text-success-foreground hover:opacity-90 disabled:opacity-50"
          >
            <CircleDollarSign className="h-3.5 w-3.5" />
            Mark paid → create bundle &amp; record sale
          </button>
          <p className="text-[10px] text-muted-foreground">
            Turns the {heldCount} held card{heldCount === 1 ? "" : "s"} into a
            bundle product and opens the sale form to record it.
          </p>
        </div>
      )}

      {/* TNK-127 phase 2 — inline send-offer form. Replaces the action
          row when toggled, so the operator can stay in the same card. */}
      {showOfferForm && (
        <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
            Send offer
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="space-y-0.5 text-[10px] text-muted-foreground">
              Subtotal
              <input
                type="number"
                step="0.01"
                min="0"
                value={offerSubtotal}
                onChange={(e) => setOfferSubtotal(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground"
              />
            </label>
            <label className="space-y-0.5 text-[10px] text-muted-foreground">
              Discount
              <input
                type="number"
                step="0.01"
                min="0"
                value={offerDiscount}
                onChange={(e) => setOfferDiscount(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground"
              />
            </label>
            <label className="space-y-0.5 text-[10px] text-muted-foreground">
              Shipping
              <input
                type="number"
                step="0.01"
                min="0"
                value={offerShipping}
                onChange={(e) => setOfferShipping(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground"
              />
            </label>
          </div>
          <div className="flex items-center justify-between rounded border border-border bg-muted/40 px-2.5 py-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">
              Total
            </span>
            <span className="text-[13px] font-bold text-foreground">
              ${offerTotal.toFixed(2)}
            </span>
          </div>
          <label className="block space-y-0.5 text-[10px] text-muted-foreground">
            Expires
            <input
              type="datetime-local"
              value={offerExpiresAt}
              onChange={(e) => setOfferExpiresAt(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground"
            />
          </label>
          <label className="block space-y-0.5 text-[10px] text-muted-foreground">
            Message (optional)
            <textarea
              rows={2}
              value={offerMessage}
              onChange={(e) => setOfferMessage(e.target.value)}
              placeholder="Anything you want the buyer to see with the offer."
              className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground"
            />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowOfferForm(false)}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-[11px] font-semibold text-foreground hover:bg-muted disabled:opacity-50"
            >
              <Undo2 className="h-3 w-3" /> Cancel
            </button>
            <button
              type="button"
              disabled={busy || offerTotal < 0}
              onClick={() => {
                onSendOffer({
                  subtotal: Number(offerSubtotal) || 0,
                  discount: Number(offerDiscount) || 0,
                  shipping: Number(offerShipping) || 0,
                  total: offerTotal,
                  message: offerMessage.trim() || undefined,
                  expiresAt: new Date(offerExpiresAt).toISOString(),
                });
                setShowOfferForm(false);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Send className="h-3 w-3" /> Send offer
            </button>
          </div>
        </div>
      )}

      {isOpen && !showOfferForm && (
        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          {canSendOffer && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowOfferForm(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              Send offer
            </button>
          )}
          {r.status === "Pending" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAct("accept")}
              className="inline-flex items-center gap-1.5 rounded-md bg-success px-3 py-1.5 text-xs font-semibold text-success-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              Quick accept
            </button>
          )}
          {r.status === "Accepted" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAct("complete")}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              Mark completed
            </button>
          )}
          {/* TNK-127 phase 2 — hide the standalone "Decline whole
              reservation" button when an offer is out. The offer panel's
              "Cancel offer" covers that case; showing both is redundant
              and confusing (operator hit this on step 4). To fully
              decline an offer-sent reservation, cancel the offer first
              (moves to UnderReview), then decline. */}
          {!hasActiveOffer && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAct("decline")}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Decline
            </button>
          )}
        </div>
      )}
    </article>
  );
}

// ── Card thumb tile ────────────────────────────────────────────────────────
// TNK-163 — rarity-tinted glow + MTG color-stripe border (mirrors the
// import wide-row treatment from TNK-140/141). Per-card decline lives
// inside this component: × button → inline confirm bar → patch.
function CardTile({
  card: c,
  busy,
  canRemove,
  onItemAct,
}: {
  card: ItemView;
  busy: boolean;
  canRemove: boolean;
  onItemAct: (itemId: string, action: ReservationItemAction) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const img = c.imageFrontRef ? imageUrl(c.imageFrontRef) : null;
  // TNK-212 phase 2b — a bundle line item (no card metadata; cover image).
  const isBundle = c.kind === "bundle";
  const isRemoved =
    c.itemStatus === "removed_unavailable" || c.itemStatus === "released";
  // TNK-127 — card sold/bundled/held elsewhere (read-side availability).
  const unavailable = !isRemoved && isReservationItemUnavailable(c);
  const isMagic = c.game === "Magic";
  const rarityCfg = c.rarity ? rarityConfig[asRarity(c.rarity)] : null;
  const mtgStripe = isMagic
    ? mtgBorderGradient(c.mtgColors ?? undefined)
    : null;

  // Card outline: rarity color wins over MTG stripe for Pokémon; for
  // Magic, the MTG color stripe wins (genre cue stronger than rarity).
  const borderColor = isRemoved
    ? "rgba(248,113,113,0.6)"
    : isMagic && mtgStripe
      ? null // gradient via background
      : rarityCfg?.color
        ? `${rarityCfg.color}55`
        : "var(--color-border)";
  // TNK-165 — every tile gets a soft drop-shadow underneath so the row
  // reads as floating cards rather than a flat grid. Composed with the
  // rarity glow when present. The shared `rarityCfg.glow` (rgba at
  // 0.25-0.4 opacity) reads as nearly invisible at 78px tile size, so
  // we derive a stronger halo from the solid `rarityCfg.color` at ~80%
  // opacity (8-digit hex `cc`). Spread `2px` makes the glow fan out
  // beyond the tile edge so it's readable next to the drop shadow.
  // Removed tiles keep the shadow but drop the rarity glow.
  const dropShadow = "0 6px 12px -2px rgba(0,0,0,0.28)";
  const strongGlow = rarityCfg ? `${rarityCfg.color}cc` : null;
  const glowStyle: { boxShadow: string } = {
    boxShadow:
      !isRemoved && strongGlow
        ? `0 0 16px 2px ${strongGlow}, ${dropShadow}`
        : dropShadow,
  };

  return (
    // TNK-163 follow-up — hover quick-view: `group` so the inner image
    // can scale on hover, `relative hover:z-30` so the enlarged image
    // pops above neighbor tiles instead of being clipped by them.
    <div
      className="group relative flex w-[78px] shrink-0 flex-col items-center gap-1 hover:z-30"
      title={`${c.name}${isBundle ? ` · Bundle of ${c.cardCount ?? "?"} cards` : c.condition ? ` · ${c.condition}` : ""} · $${itemPrice(c).toFixed(2)}${isRemoved ? " · REMOVED" : unavailable ? " · UNAVAILABLE" : ""}`}
    >
      <div className="relative w-full">
        <div
          className={`aspect-[3/4] overflow-hidden rounded-md transition-transform duration-150 ease-out group-hover:scale-[1.6] ${
            isRemoved || unavailable ? "opacity-40" : ""
          }`}
          style={{
            background:
              mtgStripe && !isRemoved ? mtgStripe : "var(--color-muted)",
            border: borderColor ? `1px solid ${borderColor}` : "none",
            padding: mtgStripe && !isRemoved ? 2 : 0,
            ...glowStyle,
          }}
        >
          <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-sm bg-muted text-muted-foreground">
            {img ? (
              <img
                src={img}
                alt={c.name}
                className={`h-full w-full object-contain ${
                  isRemoved ? "grayscale" : ""
                }`}
              />
            ) : isBundle ? (
              <Package className="h-5 w-5" />
            ) : null}
          </div>
        </div>

        {/* TNK-212 phase 2b — flag bundle tiles so the seller can tell a
            locked bundle from a single at a glance. */}
        {isBundle && !isRemoved && (
          <span className="absolute -left-1 -top-1 flex items-center gap-0.5 rounded-full bg-primary/90 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-primary-foreground shadow-md">
            <Package className="h-2.5 w-2.5" />
            Lot
          </span>
        )}

        {/* Per-card × — only shown when reservation is open + item active. */}
        {canRemove && !isRemoved && c.itemId && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming(true)}
            aria-label={`Remove ${c.name}`}
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-md transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <X className="h-3 w-3" />
          </button>
        )}

        {isRemoved && (
          <span className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive/85 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-destructive-foreground shadow-md">
            Removed
          </span>
        )}
        {unavailable && (
          <span className="absolute -right-1.5 -top-1.5 rounded-full bg-warning/90 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-warning-foreground shadow-md">
            Gone
          </span>
        )}
      </div>

      <div className="w-full text-center text-[11px] font-semibold text-foreground">
        {isRemoved ? (
          <span className="line-through text-muted-foreground">
            ${itemPrice(c).toFixed(2)}
          </span>
        ) : (
          <span>${itemPrice(c).toFixed(2)}</span>
        )}
      </div>

      {confirming && c.itemId && (
        <div className="absolute z-10 mt-[110px] w-[180px] -translate-x-1/4 space-y-1.5 rounded-md border border-border bg-popover p-2.5 shadow-lg">
          <p className="text-[11px] text-foreground">
            Remove this card from the reservation? The buyer’s status page will
            reflect the change.
          </p>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                onItemAct(c.itemId!, "remove_unavailable");
                setConfirming(false);
              }}
              className="inline-flex flex-1 items-center justify-center gap-1 rounded bg-destructive px-2 py-1 text-[10px] font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-50"
            >
              <X className="h-2.5 w-2.5" /> Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="inline-flex flex-1 items-center justify-center gap-1 rounded border border-border px-2 py-1 text-[10px] font-semibold text-foreground hover:bg-muted"
            >
              <Undo2 className="h-2.5 w-2.5" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
